import { Injectable } from '@angular/core';
import {
  EUIInteractionCommand,
  ResponsesFromUnreal,
} from '@common/helpers/constants';
import { UnrealService } from './unreal.service';
import {
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Observable,
  Subject,
  switchMap,
} from 'rxjs';
import { EmbedProductConfiguration } from '@common/types/embed-product-configuration.interface';
import { Store } from '@ngrx/store';
import { getCurrentFileVersionPath } from '../../common/store/files/files.selectors';
import { isDefined } from '../../common/helpers';
import { HttpClient } from '@angular/common/http';

import {
  Communicator,
  ConfiguratorEnvelope,
  FromMetaboxApiAction,
  FromMetaBoxAPIEnvelope,
  SendCommandToUnreal,
  WatchCallbacks,
} from '@3dsource/metabox-front-api';

import { where } from '@3dsource/utils';
import { takeUntil } from 'rxjs/operators';

declare const window: any;

@Injectable({
  providedIn: 'root',
})
export class UnrealEmbedService extends UnrealService {
  embedFile$: Subject<{ url: string; image: string }> = new Subject();
  productSlotsMaterials$: Subject<EmbedProductConfiguration> = new Subject();

  appLoaded$: Subject<void> = new Subject<void>();
  private responseEventListeners: Map<string, (data: any) => void> = new Map();

  private reset$: Subject<void> = new Subject<void>();
  constructor(
    private readonly store: Store,
    private readonly http: HttpClient,
  ) {
    super();
    this.addExternalListeners();
    this.getEmbedFile();
  }

  getEmbedFile(): void {
    this.store
      .select(getCurrentFileVersionPath)
      .pipe(
        filter(isDefined),
        switchMap((path: string) => this.fetchFile(path)),
      )
      .subscribe((data) => {
        try {
          const embedFile = JSON.parse(data);
          this.embedFile$.next({ url: embedFile.url, image: embedFile.image });
        } catch (e) {
          console.error('Invalid JSON');
          return;
        }
      });
  }

  fetchFile(path: string): Observable<string> {
    return this.http
      .get(path, { responseType: 'text' })
      .pipe(distinctUntilChanged());
  }

  override changeResolution(): void {}

  override addResponseEventListener(
    name: EUIInteractionCommand | ResponsesFromUnreal,
    listener: (data: any) => void,
  ): void {
    this.responseEventListeners.set(name, listener);
  }

  removeResponseEventListener(name: EUIInteractionCommand): void {
    this.responseEventListeners.delete(name);
  }

  private addExternalListeners(): void {}

  addExternalApi(): void {
    this.reset$.next();
    window.env3DSource = { ...window.env3DSource };
    window.env3DSource.apiReady = (api: Communicator) => {
      this.listenApi(api);
    };
    Communicator.createInstance();
  }

  private listenApi(api: Communicator) {
    //new ShowOverlayInterface(false);
    window.emitUIInteraction = (data: any) => {
      new SendCommandToUnreal(data);
    };

    new WatchCallbacks();

    const envelope$ = fromEvent<FromMetaBoxAPIEnvelope>(
      api,
      'metaboxData',
    ).pipe(takeUntil(this.reset$));

    envelope$
      .pipe(filter(where({ action: FromMetaboxApiAction.appLoaded })))
      .subscribe(() => this.appLoaded$.next());

    const metaBoxData$ = envelope$.pipe(
      filter((data) => data.action === FromMetaboxApiAction.bridge),
    );

    metaBoxData$
      .pipe(
        filter(where({ eventType: 'streamResolution' })),
        map(({ payload }) => payload as { width: number; height: number }),
      )
      .subscribe((data) => {
        this.videoSize.w = data.width;
        this.videoSize.h = data.height;
      });

    metaBoxData$
      .pipe(
        filter(where({ eventType: 'unrealCallback' })),
        map(({ payload }) => (payload as any)?.json?.commandCallback),
        filter((data) => data?.command === EUIInteractionCommand.TAKE_RENDER),
      )
      .subscribe((data) => {
        this.responseEventListeners.get(EUIInteractionCommand.TAKE_RENDER)?.(
          data,
        );
      });

    const configuratorData$ = metaBoxData$.pipe(
      filter(where({ eventType: 'configuratorData' })),
      map((data) => data.payload as ConfiguratorEnvelope),
    );

    configuratorData$.subscribe((payload) => {
      this.productSlotsMaterials$.next(this.payloadToMaterials(payload));
    });
  }

  /**
   * Wrap ConfiguratorEnvelope to EmbedProductConfiguration
   * @param configurator
   * @private
   */
  private payloadToMaterials(
    configurator: ConfiguratorEnvelope,
  ): EmbedProductConfiguration {
    const currentProduct = configurator.configurator.products.find(
      where({ productId: configurator.productId }),
    );
    const productSlots = currentProduct?.product.slots || [];

    const selectedMaterials = {
      ...configurator.productMaterialsIds[configurator.productId],
    };

    const selectedMaterialsList = Object.entries(selectedMaterials).map(
      ([key, value]) => {
        const slot = productSlots.find(where({ id: key }));

        const enabledMaterials = slot?.enabledMaterials || [];
        const material = enabledMaterials.find(where({ id: value }));
        return {
          label: material?.title || '',
          name: key,
          pakFilePath: key,
          slotLabel: slot?.label || key,
          slotName: key,
          texture: value,
        };
      },
    );

    /**
     * Required for the UI to display available materials
     */
    const availableMaterials = selectedMaterialsList.map((mat) => {
      return {
        slotLabel: mat.slotLabel,
        slotName: mat.slotName,
        variants: [],
      };
    });

    return {
      productName: currentProduct?.label || '',
      selectedMaterials: selectedMaterialsList,
      availableMaterials,
    };
  }
}
